<article
    class="surface-elevated group animate-fade-in cursor-pointer overflow-hidden p-5 transition-all duration-300 hover:-translate-y-1 hover:border-border-primary focus-within:border-primary focus-within:shadow-glow"
    style="opacity: 1; transform: none;">
    <div class="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div class="relative shrink-0">
            <img
                [src]="expert.image"
                [alt]="expert.name"
                class="h-20 w-20 rounded-xl border border-border-strong object-cover shadow-subtle transition-colors duration-300 group-hover:border-primary/60 sm:h-24 sm:w-24">
            @if (expert.isVerified) {
                <div class="absolute -bottom-1 flex h-7 w-7 items-center justify-center rounded-full border border-success/20 bg-success-soft text-success shadow-subtle ltr:-right-1 rtl:-left-1">
                    <i class="fas fa-circle-check text-xs"></i>
                </div>
            }
        </div>

        <div class="min-w-0 flex-1">
            <div class="mb-2 flex flex-wrap items-center gap-2">
                <span class="gap-1.5 uppercase" [ngClass]="getBadgeClass()">
                    <i [class]="getBadgeIcon() + ' text-[10px]'"></i>
                    {{ ('experts.types.' + expert.expertType) | translate }}
                </span>
                @if (expert.companyName) {
                    <span class="badge badge-neutral">{{ expert.companyName }}</span>
                }
                @if (expert.isNew) {
                    <span class="badge badge-error gap-1 animate-pulse border-danger-border bg-danger-soft text-danger">
                        <i class="fa-solid fa-fire text-[10px]"></i>
                        {{ 'experts.common.new' | translate }}
                    </span>
                }
            </div>

            <h3 class="mb-1 truncate text-lg font-semibold text-text-primary transition-colors duration-300 group-hover:text-primary" dir="auto">
                {{ expert.name }}
            </h3>

            <div class="mb-4 flex flex-wrap items-center gap-4 border-b border-border-subtle pb-4">
                <div class="flex items-center gap-1">
                    <i class="fas fa-star text-xs text-warning"></i>
                    <span class="text-sm font-bold text-text-primary">{{ expert.rating }}</span>
                    <span class="text-xs text-text-muted">({{ expert.reviewsCount }} {{ 'experts.card.review' | translate }})</span>
                </div>
                <div class="text-sm text-text-secondary">{{ expert.experienceYears }} {{ 'experts.card.yearsExperience' | translate }}</div>
                <div class="text-sm text-text-secondary">
                    {{ 'experts.card.since' | translate }}
                    @if (getJoinedDate().startsWith('experts.')) {
                        {{ getJoinedDate() | translate }}
                    } @else {
                        {{ getJoinedDate() }}
                    }
                </div>
            </div>

            <div class="mb-3 grid grid-cols-3 gap-2">
                <div class="rounded-lg border border-border-subtle bg-surface px-2 py-3 text-center transition-colors duration-300 group-hover:bg-surface-hover">
                    <div class="text-lg font-bold text-primary">{{ expert.propertiesCount }}</div>
                    <div class="text-[10px] font-medium uppercase text-text-muted">
                        {{ (expert.type === 'developer' ? 'experts.card.units' : 'experts.card.properties') | translate }}
                    </div>
                </div>
                <div class="rounded-lg border border-border-subtle bg-surface px-2 py-3 text-center transition-colors duration-300 group-hover:bg-surface-hover">
                    <div class="text-lg font-bold text-primary">{{ expert.dealsCount }}</div>
                    <div class="text-[10px] font-medium uppercase text-text-muted">
                        {{ (expert.type === 'developer' ? 'experts.card.projects' : 'experts.card.deals') | translate }}
                    </div>
                </div>
                <div class="rounded-lg border border-border-subtle bg-surface px-2 py-3 text-center transition-colors duration-300 group-hover:bg-surface-hover">
                    <div class="text-lg font-bold text-primary">{{ expert.ratingScore }}</div>
                    <div class="text-[10px] font-medium uppercase text-text-muted">{{ 'experts.card.rating' | translate }}</div>
                </div>
            </div>

            @if (expert.about) {
                <p class="mb-3 line-clamp-2 text-sm leading-6 text-text-secondary">{{ expert.about }}</p>
            }

            <div class="mb-3">
                <div class="flex flex-wrap gap-1">
                    @for (tag of expert.tags; track tag) {
                        @if (isKnownTag(tag)) {
                            <span class="badge badge-primary">{{ getTagTranslationKey(tag) | translate }}</span>
                        } @else {
                            <span class="badge badge-primary">{{ tag }}</span>
                        }
                    } @empty {
                        <span class="badge badge-neutral">{{ 'experts.card.noSpecializations' | translate }}</span>
                    }
                </div>
            </div>

            <div class="mb-4 grid gap-2 text-xs text-text-secondary sm:grid-cols-2">
                <div class="flex min-w-0 items-center gap-2">
                    <i class="fas fa-map-pin flex h-4 w-4 items-center justify-center text-text-muted"></i>
                    @if (expert.location.startsWith('experts.')) {
                        <span class="truncate">{{ expert.location | translate }}</span>
                    } @else {
                        <span class="truncate">{{ expert.location }}</span>
                    }
                </div>
                @if (expert.phoneNumber) {
                    <a class="flex min-w-0 items-center gap-2 transition-colors hover:text-primary" [href]="'tel:' + expert.phoneNumber">
                        <i class="fas fa-phone flex h-4 w-4 items-center justify-center text-text-muted"></i>
                        <span class="truncate" dir="ltr">{{ expert.phoneNumber }}</span>
                    </a>
                }
                @if (expert.email) {
                    <a class="flex min-w-0 items-center gap-2 transition-colors hover:text-primary sm:col-span-2" [href]="'mailto:' + expert.email">
                        <i class="fas fa-envelope flex h-4 w-4 items-center justify-center text-text-muted"></i>
                    <span class="truncate" dir="ltr">{{ expert.email }}</span>
                    </a>
                }
                @if (expert.website) {
                    <a class="flex min-w-0 items-center gap-2 transition-colors hover:text-primary sm:col-span-2" [href]="expert.website" target="_blank" rel="noopener noreferrer">
                        <i class="fas fa-globe flex h-4 w-4 items-center justify-center text-text-muted"></i>
                        <span class="truncate" dir="ltr">{{ expert.website }}</span>
                    </a>
                }
            </div>

            <div class="grid grid-cols-2 gap-2">
                <a [routerLink]="['/experts', expert.id]" [state]="{ profileUrl: expert.profileUrl }" class="btn btn-primary min-h-10 px-3 py-2 text-xs">
                    <i class="fas fa-eye text-[10px]"></i>
                    {{ 'experts.card.profile' | translate }}
                </a>
                <a
                    [href]="expert.phoneNumber ? 'tel:' + expert.phoneNumber : null"
                    class="btn btn-outline min-h-10 px-3 py-2 text-xs"
                    [class.pointer-events-none]="!expert.phoneNumber"
                    [class.opacity-50]="!expert.phoneNumber">
                    <i class="fas fa-phone text-[10px]"></i>
                    {{ 'experts.card.call' | translate }}
                </a>
            </div>
        </div>
    </div>
</article>
